"use client"

import React from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { cn } from '../../../lib/utils'
import { MapPin, Navigation, Globe, ZoomIn, ZoomOut } from 'lucide-react'
import { Button } from '../../ui/button'

interface MapLocation {
  id: string
  name: string
  lat: number
  lng: number
  value?: number
  type?: 'primary' | 'secondary' | 'warning' | 'danger'
  description?: string
}

interface MapWidgetProps {
  id: string
  title: string
  description?: string
  locations: MapLocation[]
  className?: string
  height?: number
  glassmorphism?: boolean
  compact?: boolean
  interactive?: boolean
  onLocationClick?: (location: MapLocation) => void
}

export function MapWidget({
  id,
  title,
  description,
  locations,
  className,
  height = 300,
  glassmorphism = false,
  compact = false,
  interactive = true,
  onLocationClick
}: MapWidgetProps) {
  const [selectedLocation, setSelectedLocation] = React.useState<MapLocation | null>(null)
  const [zoom, setZoom] = React.useState(1)
  
  const mapHeight = compact ? 150 : height

  // Basit harita görünümü için renk shemaları
  const getLocationColor = (type?: MapLocation['type']) => {
    switch (type) {
      case 'primary':
        return 'bg-primary text-primary-foreground'
      case 'secondary':
        return 'bg-secondary text-secondary-foreground'
      case 'warning':
        return 'bg-yellow-500 text-white'
      case 'danger':
        return 'bg-destructive text-destructive-foreground'
      default:
        return 'bg-primary text-primary-foreground'
    }
  }

  return (
    <Card className={cn(
      "relative overflow-hidden",
      glassmorphism && "bg-background/60 backdrop-blur-md border-white/10",
      className
    )}>
      {/* Header */}
      <CardHeader className={cn(
        "flex flex-row items-center justify-between space-y-0",
        compact ? "pb-1 pt-2 px-3" : "pb-3"
      )}>
        <div className="flex items-center gap-2">
          <Globe className={cn(
            "text-muted-foreground",
            compact ? "h-3.5 w-3.5" : "h-4 w-4"
          )} />
          <CardTitle className={cn(
            "font-semibold",
            compact ? "text-xs" : "text-base"
          )}>{title}</CardTitle>
        </div>
        
        {/* Zoom controls */}
        {!compact && interactive && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setZoom(Math.min(zoom + 0.2, 2))}
            >
              <ZoomIn className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setZoom(Math.max(zoom - 0.2, 0.5))}
            >
              <ZoomOut className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </CardHeader>

      {/* Map Content */}
      <CardContent className={cn(
        "p-0",
        compact ? "px-2 pb-2" : "px-4 pb-4"
      )}>
        <div 
          className="relative bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 rounded-lg overflow-hidden"
          style={{ height: mapHeight }}
        >
          {/* Basit dünya haritası arka planı */}
          <div className="absolute inset-0 opacity-20">
            <svg viewBox="0 0 100 50" className="w-full h-full">
              <path
                d="M10,25 Q20,20 30,25 T50,25 Q60,20 70,25 T90,25"
                stroke="currentColor"
                strokeWidth="0.5"
                fill="none"
                className="text-primary"
              />
              <circle cx="20" cy="20" r="8" fill="currentColor" opacity="0.1" className="text-primary" />
              <circle cx="70" cy="30" r="10" fill="currentColor" opacity="0.1" className="text-primary" />
              <circle cx="45" cy="25" r="6" fill="currentColor" opacity="0.1" className="text-primary" />
            </svg>
          </div>

          {/* Location markers */}
          <div 
            className="relative w-full h-full"
            style={{ transform: `scale(${zoom})` }}
          >
            {locations.map((location, index) => {
              // Basit pozisyon hesaplama (normalize edilmiş koordinatlar)
              const x = ((location.lng + 180) / 360) * 100
              const y = ((90 - location.lat) / 180) * 100
              
              return (
                <motion.div
                  key={location.id}
                  className="absolute"
                  style={{ left: `${x}%`, top: `${y}%` }}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <motion.button
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                    className={cn(
                      "relative flex items-center justify-center rounded-full shadow-lg transition-all",
                      compact ? "h-4 w-4" : "h-8 w-8",
                      getLocationColor(location.type),
                      selectedLocation?.id === location.id && "ring-2 ring-offset-2"
                    )}
                    onClick={() => {
                      setSelectedLocation(location)
                      onLocationClick?.(location)
                    }}
                  >
                    <MapPin className={cn(
                      "text-current",
                      compact ? "h-2.5 w-2.5" : "h-4 w-4"
                    )} />
                    
                    {/* Value badge */}
                    {location.value && !compact && (
                      <span className="absolute -top-2 -right-2 bg-background text-foreground text-xs font-bold px-1 rounded border shadow">
                        {location.value}
                      </span>
                    )}
                  </motion.button>
                  
                  {/* Location name tooltip */}
                  {!compact && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 pointer-events-none">
                      <div className="bg-popover text-popover-foreground px-2 py-1 rounded text-xs whitespace-nowrap shadow-lg opacity-0 hover:opacity-100 transition-opacity">
                        {location.name}
                      </div>
                    </div>
                  )}
                </motion.div>
              )
            })}
          </div>

          {/* Selected location info */}
          {selectedLocation && !compact && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute bottom-2 left-2 right-2 bg-background/95 backdrop-blur-sm rounded-lg p-3 shadow-lg"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-sm">{selectedLocation.name}</h4>
                  {selectedLocation.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {selectedLocation.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <Navigation className="h-3 w-3" />
                    <span>{selectedLocation.lat.toFixed(2)}, {selectedLocation.lng.toFixed(2)}</span>
                  </div>
                </div>
                {selectedLocation.value && (
                  <div className="text-right">
                    <p className="text-lg font-bold">{selectedLocation.value}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>

        {/* Location list (compact mode) */}
        {compact && locations.length > 0 && (
          <div className="mt-2 space-y-1">
            {locations.slice(0, 3).map((location) => (
              <div
                key={location.id}
                className="flex items-center justify-between text-xs cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5"
                onClick={() => {
                  setSelectedLocation(location)
                  onLocationClick?.(location)
                }}
              >
                <span className="flex items-center gap-1">
                  <MapPin className="h-2.5 w-2.5" />
                  {location.name}
                </span>
                {location.value && (
                  <span className="font-semibold">{location.value}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default MapWidget